import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePermissions } from '@/hooks/usePermissions'
import { PERMISSIONS } from '@/lib/permissions'
import { Outlet, useMatch, useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'
import { TopBar } from '../../layouts/TopBar'
import * as planningApi from '../../../lib/api/v2/planning'
import type {
  ScheduleEventDto, EmployeeDto, InboxProjectRowDto, SuiviDto, UnavailabilityDto, CreateEventBody,
  ScheduleEventKind, DurationUnit, AlertAnchorStep, PlanningProjectSearchRowDto, ConfirmationChannel,
  SlotStartBody, WorkItemStatus,
} from '../../../lib/api/v2/planning'
import { AgendaGrid } from './agenda/AgendaGrid'
import { ProjectSearchBox } from './ProjectSearchBox'
import { SchedulingFiche } from './agenda/SchedulingFiche'
import { AbsencePopover, AbsenceDetailPopover, absenceDateRange } from './agenda/AbsencePopover'
import { InboxList } from './inbox/InboxList'
import { SuiviPanel } from './suivi/SuiviPanel'
import { ProjectDetailDrawer } from './sidebar/ProjectDetailDrawer'
import { startOfWeek, isoDate, prefillForKind, kindColor, kindLabel, alertBadgesByProject, startTimeOf, assignableOnly, resolveLaneMode, type AgendaLaneMode } from './planning-helpers'
import { setPlanningSettings } from './planning-settings'
import { formatWindowLabel } from './window-label'
import { useIsDesktop, useMediaQuery } from '../../../hooks/useMediaQuery'
import { cn } from '../../../lib/utils'

interface AbsenceState {
  userId: string
  label: string
  x: number
  y: number
  /** Set when editing an existing absence: prefill + delete-then-recreate on submit. */
  editing?: UnavailabilityDto
}
interface AbsenceDetailState { absence: UnavailabilityDto; x: number; y: number }
interface FicheState {
  mode: 'create' | 'edit'
  kind: ScheduleEventKind
  projectId: string | null
  technicalSurveyId: string | null
  installationId: string | null
  unit: DurationUnit
  value: number
  dayIso: string
  startTime: string
  assignees: string[]
  /** Note of the slot — its title on a RDV / de l'interne, what happens in that block otherwise. */
  label: string
  /** Confirmation of the slot (edit mode) — drives the « Statut du créneau » block and the warning. */
  confirmedAt?: string | null
  confirmationChannel?: ConfirmationChannel | null
  confirmationNote?: string | null
  /** Lifecycle of the work item behind the slot — what ends the confirmation question (null on a
      RDV, which has no work item and never settles), plus the closure date for the label. */
  workItemStatus?: WorkItemStatus | null
  workItemClosedAt?: string | null
  eventId?: string
  /**
   * Distinguishes one blank créneau draft from the next (SOLY-PLAN-003 MS-08). Two successive
   * « + Ajouter un créneau » would otherwise share the identity `create:new:<installationId>`,
   * React would reuse the mounted fiche, and the second draft would inherit the first one's date
   * and duration — creating a créneau on a day nobody picked.
   */
  draftSeq?: number
  x: number
  y: number
}


/** À planifier / À suivre vertical split — Outlook-style, persisted across sessions. */
const SPLIT_KEY = 'soly-planning-panel-split'
/** Lane grouping (SOLY-PLAN-005) — a per-user reading preference, so localStorage, not the URL. */
const LANE_MODE_KEY = 'soly-planning-lane-mode'
const clampRatio = (r: number) => Math.min(0.85, Math.max(0.15, r))

/**
 * The Planning V1 cockpit (SOLY-PLAN-001 MS-03). Three columns: Inbox · Agenda · Suivi. Placement
 * is via the scheduling fiche (click) — drag&drop removed in MS-18. Suivi refetches on-mutation only.
 */
export function PlanningPage() {
  // SOLY-PERM-001 — `planning:read` gets the cockpit; only `planning:manage` gets to change it.
  // The agenda already reads « no callback » as « this gesture does not exist » (empty-cell quick
  // add, absence pill), so withholding the callback is the whole gate — no second mechanism.
  const { can } = usePermissions()
  const canManage = can(PERMISSIONS.PLANNING_MANAGE)
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()))
  const [weeks, setWeeks] = useState(2)
  const [events, setEvents] = useState<ScheduleEventDto[]>([])
  const [employees, setEmployees] = useState<EmployeeDto[]>([])
  const [unavailabilities, setUnavailabilities] = useState<UnavailabilityDto[]>([])
  const [holidays, setHolidays] = useState<Set<string>>(new Set())
  const [inbox, setInbox] = useState<InboxProjectRowDto[]>([])
  const [suivi, setSuivi] = useState<SuiviDto | null>(null)
  const navigate = useNavigate()
  const detailMatch = useMatch('/planning/projects/:id')
  const [searchParams] = useSearchParams()
  // The open project + the section to scroll to are URL-driven (deep-linkable, shareable, back-button).
  const openProjectId = detailMatch?.params.id ?? null
  const anchorStep = (searchParams.get('s') as AlertAnchorStep | null) ?? undefined
  // Docked detail (Elodie 2026-06-14): on desktop, an open project becomes a 640px column beside a
  // LIVE agenda. Three tiers by width: <1024 → modal overlay ; 1024–1535 → docked, the À planifier/
  // À suivre aside steps aside to make room (330+640 leaves too little agenda) ; ≥1536 → docked AND
  // the aside stays (big screens fit everything at once).
  const isDesktop = useIsDesktop()
  const isWideDesktop = useMediaQuery('(min-width: 1536px)')
  const docked = isDesktop && openProjectId !== null
  const showAside = !docked || isWideDesktop
  // Auto-scroll the agenda to a project's pose on open — once per open (don't fight manual navigation).
  const scrolledForRef = useRef<string | null>(null)
  // The slot the agenda was just pointed at — worn as a ring for a couple of seconds (see
  // showSlotInAgenda). Cleared by a timer. Re-clicking the SAME créneau does not re-flash (React
  // bails out on an identical state), which is fine: nothing moved, and the ring is still on.
  const [highlightEventId, setHighlightEventId] = useState<string | null>(null)
  const [fiche, setFiche] = useState<FicheState | null>(null)
  const [absence, setAbsence] = useState<AbsenceState | null>(null)
  const [absenceDetail, setAbsenceDetail] = useState<AbsenceDetailState | null>(null)
  const [loading, setLoading] = useState(true)
  const [agendaLoading, setAgendaLoading] = useState(true)
  const [splitRatio, setSplitRatio] = useState(() => {
    const stored = Number(localStorage.getItem(SPLIT_KEY))
    return Number.isFinite(stored) && stored > 0 ? clampRatio(stored) : 0.5
  })
  // What the reader last chose, raw — `resolveLaneMode` decides what it means (SOLY-PLAN-005 D5).
  const [storedLaneMode, setStoredLaneMode] = useState<string | null>(
    () => localStorage.getItem(LANE_MODE_KEY),
  )
  const asideRef = useRef<HTMLElement>(null)

  /** Drag the panel splitter: ratio follows the pointer, persisted on release. */
  const startSplitDrag = (e: React.PointerEvent) => {
    e.preventDefault()
    const rect = asideRef.current?.getBoundingClientRect()
    if (!rect || rect.height === 0) return
    const ratioAt = (clientY: number) => clampRatio((clientY - rect.top) / rect.height)
    const onMove = (ev: PointerEvent) => setSplitRatio(ratioAt(ev.clientY))
    const onUp = (ev: PointerEvent) => {
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', onUp)
      localStorage.setItem(SPLIT_KEY, String(ratioAt(ev.clientY)))
    }
    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointerup', onUp)
  }

  const userNameById = useMemo(() => {
    // Full roster on purpose (deactivated included): past events must always
    // resolve their assignee's name (SOLY-TEAM-001).
    const map: Record<string, string> = {}
    employees.forEach((e) => { map[e.userId] = e.displayName || e.email })
    return map
  }, [employees])

  // Assignable lists and capacity gauges only see active members.
  const activeEmployees = useMemo(() => employees.filter((e) => e.isActive), [employees])

  // The « Équipes » option exists only once a crew does: offering a view of something nobody
  // declared is a control that answers nothing. Read off the roster, so no extra request
  // (SOLY-PLAN-005). The selector itself is always there: « Types » needs no crew.
  const hasCrews = useMemo(() => employees.some((e) => e.crewId != null), [employees])
  const laneMode = resolveLaneMode(storedLaneMode, hasCrews)
  const laneModeOptions: [AgendaLaneMode, string, string][] = [
    ['type', 'Types', 'Une ligne par type — les poses, puis les métrés, puis les rendez-vous'],
    ...(hasCrews ? [['crew', 'Équipes', 'Une ligne par équipe — un chantier partagé ne se répète plus'] as [AgendaLaneMode, string, string]] : []),
    ['person', 'Personnes', 'Une ligne par personne'],
  ]
  const chooseLaneMode = (mode: AgendaLaneMode) => {
    setStoredLaneMode(mode)
    localStorage.setItem(LANE_MODE_KEY, mode)
  }

  // Agenda lanes: active members PLUS any deactivated member still assigned to
  // an event in the loaded window — otherwise that event has no lane at all
  // and silently disappears from the grid.
  const agendaEmployees = useMemo(() => {
    const assigned = new Set(events.flatMap((ev) => ev.assignments.map((a) => a.assignedUserId)))
    const inactiveAssigned = employees.filter((e) => !e.isActive && assigned.has(e.userId))
    return inactiveAssigned.length > 0 ? [...activeEmployees, ...inactiveAssigned] : activeEmployees
  }, [employees, activeEmployees, events])

  const range = useMemo(() => {
    // Full weeks (Sundays included): the grid shows Saturday always and Sunday-on-event (round 6).
    // Calendar days (SOLY-PLAN-007): `from` is the first day shown, `to` the day after the last one.
    const to = new Date(weekStart)
    to.setUTCDate(to.getUTCDate() + weeks * 7)
    return { from: isoDate(weekStart), to: isoDate(to) }
  }, [weekStart, weeks])

  // Suivi points mapped onto the work item that carries them — drives the agenda bar badges.
  const alertsByProject = useMemo(() => alertBadgesByProject(suivi?.projects ?? []), [suivi])

  const loadAgenda = useCallback(async () => {
    const [ev, un] = await Promise.all([
      planningApi.getEvents(range.from, range.to),
      planningApi.getUnavailabilities(range.from, range.to),
    ])
    setEvents(ev)
    setUnavailabilities(un)
    // Fold the fresh slot back into an OPEN fiche. The fiche holds a snapshot taken at open time,
    // and the drawer sits behind it on the same slot: recording the report there used to repaint
    // the bar and the drawer while the fiche kept offering « Confirmer » on a pose that had just
    // closed — and the click went through, because the domain guards only cancellation and kind.
    // The date-derived predicate this replaced could not go stale; a lifecycle one can.
    setFiche((f) => {
      if (!f || f.mode !== 'edit' || !f.eventId) return f
      const fresh = ev.find((e) => e.id === f.eventId)
      if (!fresh) return f
      return {
        ...f,
        confirmedAt: fresh.confirmedAt,
        confirmationChannel: fresh.confirmationChannel,
        confirmationNote: fresh.confirmationNote,
        workItemStatus: fresh.workItemStatus ?? null,
        workItemClosedAt: fresh.workItemClosedAt ?? null,
      }
    })
  }, [range])

  // Navigation (MS-20): ◄ ► always step by ONE week, whatever the amplitude. Anchor (`weekStart`)
  // and zoom (`weeks`) are two distinct concepts — the window slides, the 2nd visible week becomes
  // the 1st, so reading continuity is preserved even in 3-week mode.
  const shiftWeek = (dir: number) =>
    setWeekStart((ws) => startOfWeek(new Date(ws.getTime() + dir * 7 * 24 * 60 * 60 * 1000)))
  const gotoDate = (iso: string) => { if (iso) setWeekStart(startOfWeek(new Date(`${iso}T12:00:00Z`))) }
  const windowLabel = useMemo(() => formatWindowLabel(weekStart, weeks), [weekStart, weeks])

  /** Refetch the alert-driven panels. Called after every mutation (no polling). */
  const refetchSuiviAndInbox = useCallback(async () => {
    const [ib, sv] = await Promise.all([planningApi.getInbox(), planningApi.getSuivi()])
    setInbox(ib)
    setSuivi(sv)
  }, [])

  // Initial load (once): employees + holidays + the alert panels. The agenda window is loaded by a
  // separate effect, so paging the agenda never re-fetches employees/inbox/suivi nor blanks the page.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    void (async () => {
      try {
        const year = new Date().getUTCFullYear()
        // Work-day settings ride along with the holidays: both are org calendar parameters the
        // backend owns (SOLY-PLAN-003 MS-05). They must land before any placement control renders,
        // which is why they are part of the boot load and not fetched inside the fiche.
        const [emp, hol, settings] = await Promise.all([
          planningApi.getEmployees(),
          planningApi.getHolidays(year),
          planningApi.getPlanningSettings(),
        ])
        if (cancelled) return
        setEmployees(emp)
        setHolidays(new Set(hol))
        setPlanningSettings(settings)
        await refetchSuiviAndInbox()
      } catch {
        if (!cancelled) toast.error('Erreur de chargement du planning')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [refetchSuiviAndInbox])

  // Agenda window: refetch only the events/unavailabilities when the window changes (◄ ► one week at
  // a time, 1/2/3 sem amplitude, Aujourd'hui, Aller-à). The grid keeps its current events until the
  // new ones land — navigation swaps the agenda in place instead of refreshing the whole page.
  useEffect(() => {
    let cancelled = false
    setAgendaLoading(true)
    void (async () => {
      try { await loadAgenda() }
      catch { if (!cancelled) toast.error("Erreur de chargement de l'agenda") }
      finally { if (!cancelled) setAgendaLoading(false) }
    })()
    return () => { cancelled = true }
  }, [loadAgenda])

  const afterMutation = useCallback(async () => {
    await Promise.all([loadAgenda(), refetchSuiviAndInbox()])
  }, [loadAgenda, refetchSuiviAndInbox])

  // ── Detail drawer open/close helpers (URL-driven) ───────────────────────────
  // openDetail: once the drawer loads, onDetailLoaded jumps the agenda to the project's pose week
  // (inbox / suivi — you're acting on an alert, so surface the relevant week).
  const openDetail = (projectId: string, step?: AlertAnchorStep) =>
    navigate(`/planning/projects/${projectId}${step ? `?s=${step}` : ''}`)
  // openDetailNoJump: open the drawer but leave the agenda where it is — used when the agenda already
  // shows what the user clicked (an event bar), so jumping to the pose week would be disorienting.
  // Pre-claiming scrolledForRef makes onDetailLoaded's once-per-open guard short-circuit (the same
  // suppression openFromSearch relies on when it scrolls the agenda itself).
  const openDetailNoJump = (projectId: string) => {
    scrolledForRef.current = projectId
    openDetail(projectId)
  }
  const closeDetail = () => { scrolledForRef.current = null; navigate('/planning') }

  // The focus ring is a signal, not a state: it fades on its own. Re-armed on every focus (the id
  // is the dependency), so clicking two créneaux in a row flashes both.
  //
  // 🔴 The countdown starts when the bar is PAINTED, not when the id is set. The grid deliberately
  // keeps the previous week's events until the new fetch resolves (see the agenda window effect
  // below), so a jump to a week that takes more than 2,5 s to load used to clear the ring before
  // the bar it points at had ever been drawn. The acknowledgement disappeared exactly when the app
  // was slow enough to need one — and this gesture exists BECAUSE landing on an already-displayed
  // week changes nothing on screen and reads as a dead button (see showSlotInAgenda).
  //
  // Found by TC-032 « cockpit seul » on a CI agent, where the week fetch runs past 2,5 s; green on
  // a fast machine, which is why it read as flakiness for two days.
  //
  // The un-painted branch still expires, on a longer grace: if the user navigates elsewhere before
  // the week arrives, an id kept for ever would flash a ring on some unrelated later visit.
  useEffect(() => {
    if (!highlightEventId) return
    const painted = events.some((e) => e.id === highlightEventId)
    const t = setTimeout(() => setHighlightEventId(null), painted ? 2500 : 10_000)
    return () => clearTimeout(t)
  }, [highlightEventId, events])

  // When the drawer's detail has loaded, jump the agenda to the project's planned pose (once per open).
  const onDetailLoaded = useCallback((poseStartIso: string | null) => {
    if (!openProjectId || scrolledForRef.current === openProjectId) return
    scrolledForRef.current = openProjectId
    if (poseStartIso) setWeekStart(startOfWeek(new Date(`${poseStartIso}T12:00:00Z`)))
  }, [openProjectId])

  // Search → open the project and jump the agenda to its LAST planned element (pose or métré, whichever
  // is latest — BE computes it). Pre-claim scrolledForRef so onDetailLoaded won't re-jump to the pose
  // week; if nothing is planned yet we leave the current week and the drawer shows « à planifier ».
  const openFromSearch = (row: PlanningProjectSearchRowDto) => {
    if (row.lastPlannedEventStart) {
      scrolledForRef.current = row.projectId
      setWeekStart(startOfWeek(new Date(`${row.lastPlannedEventStart.slice(0, 10)}T12:00:00Z`)))
    }
    openDetail(row.projectId)
  }

  // ── Fiche open helpers ──────────────────────────────────────────────────────
  const openCellCreate = (args: { userId: string | null; day: Date; startTime: string; x: number; y: number }) => {
    const pf = prefillForKind('Appointment')
    setFiche({
      mode: 'create', kind: 'Appointment', projectId: null, technicalSurveyId: null, installationId: null,
      unit: pf.unit, value: pf.value, dayIso: isoDate(args.day), startTime: args.startTime,
      // The lane may belong to a DEACTIVATED member — the grid keeps their lane so their past events
      // do not vanish. Creating from it must not hand them new work (décision Thierry 2026-09-10).
      assignees: assignableOnly(args.userId ? [args.userId] : [], employees), label: '', x: args.x, y: args.y,
    })
  }

  const openEditEvent = (ev: ScheduleEventDto) => {
    setFiche({
      mode: 'edit', eventId: ev.id, kind: ev.kind, projectId: ev.projectId,
      technicalSurveyId: ev.technicalSurveyId, installationId: ev.installationId,
      unit: ev.durationUnit, value: ev.durationValue, dayIso: ev.startDate,
      startTime: startTimeOf(ev), assignees: ev.assignments.map((a) => a.assignedUserId),
      label: ev.label ?? '', confirmedAt: ev.confirmedAt,
      confirmationChannel: ev.confirmationChannel, confirmationNote: ev.confirmationNote,
      workItemStatus: ev.workItemStatus ?? null, workItemClosedAt: ev.workItemClosedAt ?? null,
      x: Math.round(window.innerWidth / 2 - 144), y: 120,
    })
  }

  // SOLY-PLAN-003 MS-08 — one MORE créneau on a pose already being planned. Same crew as the block
  // it was launched from (décision 5), and a blank date: the whole point of the gesture is that the
  // second date is not known yet (scénario S4). The fiche stays where it is on screen.
  const openAddSlot = (args: { installationId: string; projectId: string; assignees: string[] }) => {
    setFiche((f) => ({
      mode: 'create', kind: 'Installation', projectId: args.projectId, technicalSurveyId: null,
      installationId: args.installationId,
      // A new créneau starts at one day: the budget recap says how much is left, and the user sets
      // the rest. Prefilling the REMAINDER would silently place work on a date nobody chose.
      unit: 'Days', value: 1, dayIso: '', startTime: prefillForKind('Installation').startTime,
      // Same crew as the block it was launched from (décision 5) — minus anyone who has since left.
      assignees: assignableOnly(args.assignees, employees), label: '',
      draftSeq: (f?.draftSeq ?? 0) + 1,
      x: f?.x ?? Math.round(window.innerWidth / 2 - 144), y: f?.y ?? 120,
    }))
  }

  // Jump the open fiche to another créneau of the same pose (the list under the budget recap). The
  // fiche hands over the slot it read from the project detail, NOT just its id: that list covers
  // every créneau of the pose, including one three weeks out, while `events` only holds the loaded
  // agenda window — looking it up there would make the click do nothing on exactly the slots the
  // multi-créneaux feature exists for. The agenda follows along so the user sees where they landed.
  const openSlot = (slot: ScheduleEventDto) => {
    openEditEvent(slot)
    showSlotInAgenda(slot)
  }

  /**
   * « Voir dans l'agenda » — from a créneau card of the cockpit (MS-12, revised 2026-08-18), and the
   * week move openSlot performs on top of opening the fiche.
   *
   * From the cockpit it moves the week and STOPS there. It used to call openSlot, so the gesture
   * opened the scheduling fiche over the grid it had just moved — Élodie asks to LOOK, and got a
   * form to dismiss. The fiche's own slot list keeps openSlot: there, opening the fiche IS the
   * intention. Two intentions, two entry points; they shared one only because the week move was
   * written once.
   *
   * The flash is not decoration. Without the fiche popping up, a gesture that lands on a week
   * ALREADY displayed changes nothing on screen, and reads as a dead button — the user cannot tell
   * « nothing happened » from « you are already looking at it ».
   */
  const showSlotInAgenda = (slot: ScheduleEventDto) => {
    setWeekStart(startOfWeek(new Date(`${slot.startDate}T12:00:00Z`)))
    setHighlightEventId(slot.id)
  }

  // ── Mutations (fiche delegates here; throws propagate to show inline errors) ──
  const ficheCreate = async (body: CreateEventBody) => {
    await planningApi.createEvent(body)
    await afterMutation()
    toast.success(
      body.kind === 'Installation' ? 'Pose planifiée'
        : body.kind === 'TechnicalSurvey' ? 'Métré planifié'
          : body.kind === 'InternalWork' ? 'Créneau interne planifié'
            : 'RDV planifié')
  }

  /** Note d'un créneau — geste propre, hors replanification (SOLY-PLAN-004). */
  const ficheSetNote = async (id: string, note: string | null) => {
    const updated = await planningApi.setEventNote(id, note)
    setFiche((f) => (f && f.eventId === id ? { ...f, label: updated.label ?? '' } : f))
    await afterMutation()
  }

  const ficheReschedule = async (id: string, body: { durationUnit: DurationUnit; durationValue: number; assignedUserIds: string[]; reason?: string } & SlotStartBody) => {
    await planningApi.rescheduleEvent(id, body)
    await afterMutation()
    toast.success('Événement replanifié')
  }

  // Confirmation gestures — the fiche is the only surface a RDV can be confirmed from (no project,
  // so no drawer). The refreshed slot from the response is folded back into the open fiche, so the
  // block shows the SERVER's state rather than an optimistic guess.
  const ficheConfirm = async (id: string, channel: ConfirmationChannel, note: string | undefined) => {
    const updated = await planningApi.confirmEvent(id, channel, note)
    setFiche((f) => (f && f.eventId === id
      ? { ...f, confirmedAt: updated.confirmedAt, confirmationChannel: updated.confirmationChannel, confirmationNote: updated.confirmationNote }
      : f))
    await afterMutation()
  }

  const ficheUnconfirm = async (id: string) => {
    const updated = await planningApi.unconfirmEvent(id)
    setFiche((f) => (f && f.eventId === id
      ? { ...f, confirmedAt: updated.confirmedAt, confirmationChannel: updated.confirmationChannel, confirmationNote: updated.confirmationNote }
      : f))
    await afterMutation()
  }

  const ficheConvertKind = async (id: string, kind: ScheduleEventKind) => {
    const updated = await planningApi.convertEventKind(id, kind)
    // The reclassement may have erased the confirmation server-side — fold the SERVER's answer back
    // into the open fiche rather than guessing what it did.
    setFiche((f) => (f && f.eventId === id
      ? { ...f, kind: updated.kind, confirmedAt: updated.confirmedAt, confirmationChannel: updated.confirmationChannel, confirmationNote: updated.confirmationNote,
          // A reclassement only ever lands on RDV or Interne, and neither has a work item — so the
          // slot is definitionally unsettled. The mutation DTO does not carry these fields (the
          // agenda and cockpit reads serve them); reading them here would write undefined whatever
          // the truth was.
          workItemStatus: null, workItemClosedAt: null }
      : f))
    await afterMutation()
    toast.success(kind === 'InternalWork' ? 'Reclassé en Interne' : 'Reclassé en RDV')
  }

  const ficheCancel = async (id: string, reason: string) => {
    await planningApi.cancelEvent(id, reason)
    await afterMutation()
    toast.success('Événement annulé')
  }

  const handleAbsenceCreate = async (body: { userId: string; label: string; durationUnit: 'Hours' | 'Days'; durationValue: number } & SlotStartBody) => {
    // Edit = recreate-then-delete (no PUT endpoint). Create first: if it fails nothing is lost.
    await planningApi.createUnavailability(body)
    if (absence?.editing) await planningApi.deleteUnavailability(absence.editing.id)
    await loadAgenda()
    toast.success(absence?.editing ? 'Absence modifiée' : 'Absence enregistrée')
  }

  const handleAbsenceDelete = async (a: UnavailabilityDto) => {
    await planningApi.deleteUnavailability(a.id)
    await loadAgenda()
    toast.success('Absence supprimée')
  }

  /** Detail popover → edit: reopen the create popover prefilled at the same anchor. */
  const openAbsenceEdit = (a: UnavailabilityDto) => {
    if (!absenceDetail) return
    setAbsence({
      userId: a.userId,
      label: userNameById[a.userId] ?? '',
      x: absenceDetail.x,
      y: absenceDetail.y,
      editing: a,
    })
    setAbsenceDetail(null)
  }

  return (
    <div className="flex h-screen flex-col" data-testid="planning-page">
      <TopBar />
      <div
        className="grid flex-1 overflow-hidden"
        style={{ gridTemplateColumns: docked ? (isWideDesktop ? '330px 1fr 640px' : 'minmax(0,1fr) 640px') : '330px 1fr' }}
      >
        {/* left: À planifier (top) + À suivre (bottom), resizable via the splitter. Steps aside while a
            project is docked open on a 1024–1535 screen; on ≥1536 it stays (everything fits). */}
        {showAside && (
        <aside ref={asideRef} className="flex flex-col overflow-hidden border-r">
          <div className="min-h-0 overflow-auto" style={{ flex: `${splitRatio} 1 0%` }}>
            <InboxList rows={inbox} loading={loading} onProjectClick={(id) => openDetail(id)} />
          </div>
          <div
            role="separator"
            aria-orientation="horizontal"
            aria-label="Redimensionner À planifier / À suivre"
            data-testid="planning-panel-splitter"
            onPointerDown={startSplitDrag}
            className="group relative z-10 flex h-1.5 flex-none cursor-row-resize items-center justify-center border-y bg-muted/60 hover:bg-muted"
          >
            <span className="h-0.5 w-8 rounded-full bg-slate-300 transition group-hover:bg-slate-400" aria-hidden />
          </div>
          <div className="min-h-0 overflow-auto" style={{ flex: `${1 - splitRatio} 1 0%` }}>
            <SuiviPanel data={suivi} loading={loading} onAlertClick={(group, step) => openDetail(group.projectId, step as AlertAnchorStep)} />
          </div>
        </aside>
        )}

        {/* center: controls + strip + agenda */}
        <main className="flex flex-col overflow-hidden p-3">
          <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="planning-view-controls">
            <ProjectSearchBox onSelectProject={openFromSearch} onSelectEvent={openSlot} />
            <button
              type="button"
              onClick={() => setWeekStart(startOfWeek(new Date()))}
              className="rounded-lg border px-3 py-1.5 text-xs font-semibold hover:bg-muted"
              data-testid="planning-today-btn"
            >
              Aujourd'hui
            </button>
            {/* Week-by-week navigation (MS-20 N1) — the window slides one week per click, whatever
                the amplitude selected on the right. */}
            <div className="inline-flex items-center gap-1">
              <button type="button" onClick={() => shiftWeek(-1)} data-testid="planning-prev"
                aria-label="Semaine précédente" title="Semaine précédente" className="rounded-lg border px-2 py-1.5 hover:bg-muted">
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <span className="min-w-[200px] whitespace-nowrap text-center text-xs font-semibold" data-testid="planning-window-label">{windowLabel}</span>
              <button type="button" onClick={() => shiftWeek(1)} data-testid="planning-next"
                aria-label="Semaine suivante" title="Semaine suivante" className="rounded-lg border px-2 py-1.5 hover:bg-muted">
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="inline-flex overflow-hidden rounded-lg border">
              {([1, 2, 3] as const).map((w) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => setWeeks(w)}
                  data-testid={`planning-weeks-${w}`}
                  className={cn('border-l px-3 py-1.5 text-xs first:border-l-0',
                    weeks === w ? 'bg-primary font-semibold text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}
                >
                  {w} sem
                </button>
              ))}
            </div>
            {/* Lane grouping (SOLY-PLAN-005). Sits right after the window controls: it changes what
                the rows ARE, which is of the same nature as how many weeks are shown — and unlike
                the legend, it is a control, not a caption. */}
            {/* Always rendered: « Types » needs no crew. « Équipes » is still
                offered only once a crew exists (a view of something nobody declared answers nothing). */}
            <div className="inline-flex overflow-hidden rounded-lg border" data-testid="planning-lane-mode">
              {laneModeOptions.map(([mode, label, hint]) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => chooseLaneMode(mode)}
                  data-testid={`planning-lane-mode-${mode}`}
                  aria-pressed={laneMode === mode}
                  title={hint}
                  className={cn('border-l px-3 py-1.5 text-xs first:border-l-0',
                    laneMode === mode ? 'bg-primary font-semibold text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}
                >
                  {label}
                </button>
              ))}
            </div>
            {/* kind legend: the bars no longer spell out Pose/Métré (colour + border carry it) */}
            {/* 4 pastilles + 2 états depuis SOLY-PLAN-002 : la légende WRAPPE plutôt que de pousser
                le champ « Aller à » hors de l'écran sur un portable (piège du ship SOLY-PO-001). */}
            <div className="ml-auto flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-[11px] text-muted-foreground" data-testid="planning-legend">
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: kindColor('Installation') }} aria-hidden />
                {kindLabel('Installation')}
              </span>
              {/* SOLY-REGIE-001 — the amber bar now covers every INTERVENTION: a retouche on its own
                  file as well as a finition on a file already sold. « Finition » named only half of
                  what the colour meant the day interventions arrived. */}
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: '#d97706' }} aria-hidden />
                Intervention
              </span>
              {(['TechnicalSurvey', 'Appointment', 'InternalWork'] as const).map((k) => (
                <span key={k} className="inline-flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: kindColor(k) }} aria-hidden />
                  {kindLabel(k)}
                </span>
              ))}
              {/* Confirmed (solid) vs proposed (pale) — one hue, only its weight moves. It replaces a
                  hatch the whole team said, unanimously, they could not read on a 24px bar
                  (2026-09-01). « à confirmer » asked « par qui ? » at the exact moment the answer
                  stopped being « le client » (SOLY-PLAN-002).
                  Drawn in the POSE blue, which is what the grid actually wears. The first version
                  sampled slate — and picked bg-slate-100 for « proposé », which is the fill of a
                  CONFIRMED RDV, next to a solid slate-500 no bar has ever worn. A legend that
                  teaches the wrong pairing is worse than no legend. */}
              <span className="ml-1 inline-flex items-center gap-1 border-l pl-3">
                <span className="h-2.5 w-3 rounded-sm bg-blue-600" aria-hidden />
                confirmé
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-2.5 w-3 rounded-sm border-l-4 border-l-blue-600 bg-blue-50" aria-hidden />
                proposé
              </span>
            </div>
            <label className="flex items-center gap-1 text-xs text-muted-foreground">
              Aller à
              <input type="date" onChange={(e) => gotoDate(e.target.value)} data-testid="planning-goto"
                className="rounded-lg border px-2 py-1 text-xs text-foreground" />
            </label>
          </div>
          <div className="mt-3 flex-1 overflow-auto">
            {loading || (agendaLoading && events.length === 0) ? (
              <div className="p-6 text-sm text-muted-foreground" data-testid="planning-agenda-loading">Chargement de l'agenda…</div>
            ) : (
              <div className={cn('transition-opacity', agendaLoading && 'opacity-60')} aria-busy={agendaLoading}>
                <AgendaGrid
                  startDate={weekStart}
                  weekCount={weeks}
                  events={events}
                  employees={agendaEmployees}
                  laneMode={laneMode}
                  unavailabilities={unavailabilities}
                  holidays={holidays}
                  alertsByProject={alertsByProject}
                  highlightEventId={highlightEventId}
                  onEventClick={(ev) => ev.projectId ? openDetailNoJump(ev.projectId) : openEditEvent(ev)}
                  onEventEdit={openEditEvent}
                  onQuickAdd={canManage ? openCellCreate : undefined}
                  onAddAbsence={canManage ? setAbsence : undefined}
                  onAbsenceClick={setAbsenceDetail}
                />
              </div>
            )}
          </div>
        </main>

        {/* Project detail (Elodie 2026-06-14): docked → a real 640px column beside the LIVE agenda;
            otherwise a modal Sheet (portal → no inline box, grid stays 2-col). Open-state URL-driven. */}
        <ProjectDetailDrawer
          projectId={openProjectId}
          open={openProjectId !== null}
          docked={docked}
          onClose={closeDetail}
          onMutated={() => void afterMutation()}
          onLoaded={onDetailLoaded}
          userNameById={userNameById}
          employees={activeEmployees}
          anchorStep={anchorStep}
          /* MS-12 — the cockpit's créneau cards reuse the jump the agenda's own slot list already
             uses: same week move, same fiche, one implementation. */
          onFocusSlot={showSlotInAgenda}
        />
      </div>

      {/* Nested route placeholder (/planning/projects/:id) — the drawer above reads the match. */}
      <Outlet />

      {fiche && (
        <SchedulingFiche
          /* SOLY-PLAN-003 MS-08 — the fiche keeps its editable fields in useState, initialised ONCE
             at mount. Since MS-08 the parent can swap what the open fiche points at (« + Ajouter un
             créneau », or jumping to another créneau of the same pose) — without a key React would
             reuse the mounted instance and the new target would inherit the previous one's date,
             duration and crew. That is not a cosmetic glitch: it would reschedule the wrong
             créneau, or create one on a date nobody chose. The identity is what the fiche edits. */
          key={`${fiche.mode}:${fiche.eventId ?? `new${fiche.draftSeq ?? 0}`}:${fiche.installationId ?? ''}`}
          x={fiche.x}
          y={fiche.y}
          mode={fiche.mode}
          kind={fiche.kind}
          projectId={fiche.projectId}
          technicalSurveyId={fiche.technicalSurveyId}
          installationId={fiche.installationId}
          initialUnit={fiche.unit}
          initialValue={fiche.value}
          initialDayIso={fiche.dayIso}
          initialStartTime={fiche.startTime}
          initialAssignees={fiche.assignees}
          initialLabel={fiche.label}
          confirmedAt={fiche.confirmedAt}
          confirmationChannel={fiche.confirmationChannel}
          confirmationNote={fiche.confirmationNote}
          workItemStatus={fiche.workItemStatus}
          workItemClosedAt={fiche.workItemClosedAt}
          onConfirm={ficheConfirm}
          onUnconfirm={ficheUnconfirm}
          onConvertKind={ficheConvertKind}
          employees={activeEmployees}
          holidays={holidays}
          eventId={fiche.eventId}
          onClose={() => setFiche(null)}
          onCreate={ficheCreate}
          onReschedule={ficheReschedule}
          onCancelEvent={ficheCancel}
          onSetNote={ficheSetNote}
          onAddSlot={canManage ? openAddSlot : undefined}
          onOpenSlot={openSlot}
        />
      )}

      {absence && (
        <AbsencePopover
          x={absence.x}
          y={absence.y}
          userId={absence.userId}
          label={absence.label}
          initial={absence.editing ? { reason: absence.editing.label, ...absenceDateRange(absence.editing) } : undefined}
          onClose={() => setAbsence(null)}
          onCreate={handleAbsenceCreate}
        />
      )}

      {absenceDetail && (
        <AbsenceDetailPopover
          x={absenceDetail.x}
          y={absenceDetail.y}
          absence={absenceDetail.absence}
          userLabel={userNameById[absenceDetail.absence.userId] ?? ''}
          onClose={() => setAbsenceDetail(null)}
          onEdit={openAbsenceEdit}
          onDelete={handleAbsenceDelete}
        />
      )}
    </div>
  )
}

export default PlanningPage
